/**
 * Dados de emojis organizados por categoria para uso no chat
 * 
 * Emojis compatíveis com WhatsApp, organizados em categorias populares.
 * Cada categoria tem um ícone representativo e array de emojis.
 */

/** 6 emojis principais para reações rápidas (padrão WhatsApp Web) */
export const QUICK_REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

/** Estrutura de uma categoria de emojis */
export interface EmojiCategory {
  /** Identificador único da categoria */
  id: string;
  /** Nome exibido da categoria em português */
  label: string;
  /** Emoji usado como ícone da categoria */
  icon: string;
  /** Lista de emojis da categoria */
  emojis: string[];
}

/** Emojis mais usados para reações rápidas (expandido de 6 para 24) */
export const REACTION_EMOJIS = [
  '❤️', '👍', '👎', '😂', '😮', '😢', 
  '🙏', '🔥', '👏', '🎉', '😍', '🤔',
  '😡', '👀', '💯', '🙌', '✨', '💪',
  '🤣', '😎', '🥰', '😭', '🙄', '👌'
];

/** Todas as categorias de emojis disponíveis */
export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    id: 'smileys',
    label: 'Rostos',
    icon: '😀',
    emojis: [
      '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
      '🥲', '☺️', '😊', '😇', '🙂', '🙃', '😉', '😌',
      '😍', '🥰', '😘', '😗', '😚', '😙', '🥹', '😋',
      '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫',
      '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒',
      '🙄', '😬', '😮‍💨', '🤥', '😌', '😔', '😪', '🤤',
      '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵',
      '🥶', '🥴', '😵', '😵‍💫', '🤯', '🤠', '🥳', '🥸',
      '😎', '🤓', '🧐', '😕', '😟', '🙁', '☹️', '😮',
      '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰',
      '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓',
      '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈',
      '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻',
      '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼',
      '😽', '🙀', '😿', '😾', '🙈', '🙉', '🙊'
    ]
  },
  {
    id: 'hearts',
    label: 'Corações',
    icon: '❤️',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
      '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖',
      '💘', '💝', '💟', '♥️', '❤️‍🔥', '❤️‍🩹', '🫀', '🫶'
    ]
  },
  {
    id: 'hands',
    label: 'Mãos',
    icon: '👋',
    emojis: [
      '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏',
      '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆',
      '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛',
      '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️',
      '💅', '🤳', '💪', '🦾', '🦿', '🦵', '🦶', '👂',
      '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀',
      '👁️', '👅', '👄', '🫦'
    ]
  },
  {
    id: 'people',
    label: 'Pessoas',
    icon: '👤',
    emojis: [
      '👶', '👧', '🧒', '👦', '👩', '🧑', '👨', '👩‍🦱',
      '🧑‍🦱', '👨‍🦱', '👩‍🦰', '🧑‍🦰', '👨‍🦰', '👱‍♀️', '👱', '👱‍♂️',
      '👩‍🦳', '🧑‍🦳', '👨‍🦳', '👩‍🦲', '🧑‍🦲', '👨‍🦲', '🧔‍♀️', '🧔',
      '🧔‍♂️', '👵', '🧓', '👴', '👲', '👳‍♀️', '👳', '👳‍♂️',
      '🧕', '👮‍♀️', '👮', '👮‍♂️', '👷‍♀️', '👷', '👷‍♂️', '💂‍♀️',
      '💂', '💂‍♂️', '🕵️‍♀️', '🕵️', '🕵️‍♂️', '👩‍⚕️', '🧑‍⚕️', '👨‍⚕️',
      '👩‍🌾', '🧑‍🌾', '👨‍🌾', '👩‍🍳', '🧑‍🍳', '👨‍🍳', '👩‍🎓', '🧑‍🎓'
    ]
  },
  {
    id: 'animals',
    label: 'Animais',
    icon: '🐾',
    emojis: [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼',
      '🐻‍❄️', '🐨', '🐯', '🦁', '🐮', '🐷', '🐽', '🐸',
      '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦',
      '🐤', '🐣', '🐥', '🦆', '🦅', '🦉', '🦇', '🐺',
      '🐗', '🐴', '🦄', '🐝', '🪱', '🐛', '🦋', '🐌',
      '🐞', '🐜', '🪰', '🪲', '🪳', '🦟', '🦗', '🕷️',
      '🕸️', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙',
      '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬',
      '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍'
    ]
  },
  {
    id: 'nature',
    label: 'Natureza',
    icon: '🌸',
    emojis: [
      '🌸', '💮', '🏵️', '🌹', '🥀', '🌺', '🌻', '🌼',
      '🌷', '🌱', '🪴', '🌲', '🌳', '🌴', '🌵', '🌾',
      '🌿', '☘️', '🍀', '🍁', '🍂', '🍃', '🪹', '🪺',
      '🍇', '🍈', '🍉', '🍊', '🍋', '🍌', '🍍', '🥭',
      '🍎', '🍏', '🍐', '🍑', '🍒', '🍓', '🫐', '🥝',
      '🍅', '🫒', '🥥', '🥑', '🍆', '🥔', '🥕', '🌽',
      '🌶️', '🫑', '🥒', '🥬', '🥦', '🧄', '🧅', '🍄'
    ]
  },
  {
    id: 'food',
    label: 'Comidas',
    icon: '🍕',
    emojis: [
      '🍕', '🍔', '🍟', '🌭', '🥪', '🌮', '🌯', '🫔',
      '🥙', '🧆', '🥚', '🍳', '🥘', '🍲', '🫕', '🥣',
      '🥗', '🍿', '🧈', '🧂', '🥫', '🍱', '🍘', '🍙',
      '🍚', '🍛', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤',
      '🍥', '🥮', '🍡', '🥟', '🥠', '🥡', '🦀', '🦞',
      '🦐', '🦑', '🦪', '🍦', '🍧', '🍨', '🍩', '🍪',
      '🎂', '🍰', '🧁', '🥧', '🍫', '🍬', '🍭', '🍮',
      '🍯', '🍼', '🥛', '☕', '🫖', '🍵', '🍶', '🍾'
    ]
  },
  {
    id: 'activities',
    label: 'Atividades',
    icon: '⚽',
    emojis: [
      '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉',
      '🥏', '🎱', '🪀', '🏓', '🏸', '🏒', '🏑', '🥍',
      '🏏', '🪃', '🥅', '⛳', '🪁', '🏹', '🎣', '🤿',
      '🥊', '🥋', '🎽', '🛹', '🛼', '🛷', '⛸️', '🥌',
      '🎿', '⛷️', '🏂', '🪂', '🏋️‍♀️', '🏋️', '🏋️‍♂️', '🤸‍♀️',
      '🤸', '🤸‍♂️', '⛹️‍♀️', '⛹️', '⛹️‍♂️', '🤺', '🤾‍♀️', '🤾',
      '🏌️‍♀️', '🏌️', '🏌️‍♂️', '🏇', '🧘‍♀️', '🧘', '🧘‍♂️', '🏄‍♀️',
      '🏄', '🏄‍♂️', '🏊‍♀️', '🏊', '🏊‍♂️', '🤽‍♀️', '🤽', '🤽‍♂️'
    ]
  },
  {
    id: 'travel',
    label: 'Viagens',
    icon: '🚗',
    emojis: [
      '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑',
      '🚒', '🚐', '🛻', '🚚', '🚛', '🚜', '🦯', '🦽',
      '🦼', '🛴', '🚲', '🛵', '🏍️', '🛺', '🚨', '🚔',
      '🚍', '🚘', '🚖', '🚡', '🚠', '🚟', '🚃', '🚋',
      '🚞', '🚝', '🚄', '🚅', '🚈', '🚂', '🚆', '🚇',
      '🚊', '🚉', '✈️', '🛫', '🛬', '🛩️', '💺', '🛰️',
      '🚀', '🛸', '🚁', '🛶', '⛵', '🚤', '🛥️', '🛳️',
      '⛴️', '🚢', '⚓', '🪝', '⛽', '🚧', '🚦', '🚥'
    ]
  },
  {
    id: 'objects',
    label: 'Objetos',
    icon: '💡',
    emojis: [
      '💡', '🔦', '🏮', '🪔', '📱', '📲', '💻', '🖥️',
      '🖨️', '⌨️', '🖱️', '🖲️', '💾', '💿', '📀', '📼',
      '📷', '📸', '📹', '🎥', '📽️', '🎞️', '📞', '☎️',
      '📟', '📠', '📺', '📻', '🎙️', '🎚️', '🎛️', '🧭',
      '⏱️', '⏲️', '⏰', '🕰️', '⌛', '⏳', '📡', '🔋',
      '🔌', '💰', '🪙', '💴', '💵', '💶', '💷', '💸',
      '💳', '🧾', '💎', '⚖️', '🪜', '🧰', '🪛', '🔧',
      '🔨', '⚒️', '🛠️', '⛏️', '🪚', '🔩', '⚙️', '🗜️'
    ]
  },
  {
    id: 'symbols',
    label: 'Símbolos',
    icon: '✨',
    emojis: [
      '✨', '⭐', '🌟', '💫', '⚡', '🔥', '💥', '☀️',
      '🌤️', '⛅', '🌥️', '☁️', '🌦️', '🌧️', '⛈️', '🌩️',
      '🌨️', '❄️', '☃️', '⛄', '🌬️', '💨', '🌪️', '🌈',
      '☔', '💧', '💦', '🌊', '✅', '☑️', '✔️', '❌',
      '❎', '➕', '➖', '➗', '✖️', '💲', '💯', '🔴',
      '🟠', '🟡', '🟢', '🔵', '🟣', '🟤', '⚫', '⚪',
      '🟥', '🟧', '🟨', '🟩', '🟦', '🟪', '🟫', '⬛',
      '⬜', '◼️', '◻️', '◾', '◽', '▪️', '▫️', '🔶'
    ]
  },
  {
    id: 'flags',
    label: 'Bandeiras',
    icon: '🏳️',
    emojis: [
      '🏳️', '🏴', '🏁', '🚩', '🏳️‍🌈', '🏳️‍⚧️', '🇧🇷', '🇺🇸',
      '🇵🇹', '🇪🇸', '🇫🇷', '🇩🇪', '🇮🇹', '🇬🇧', '🇯🇵', '🇨🇳',
      '🇰🇷', '🇮🇳', '🇷🇺', '🇲🇽', '🇦🇷', '🇨🇴', '🇨🇱', '🇵🇪',
      '🇻🇪', '🇪🇨', '🇺🇾', '🇵🇾', '🇧🇴', '🇨🇦', '🇦🇺', '🇳🇿'
    ]
  }
];

/** Retorna todos os emojis de todas as categorias em um array único */
export const getAllEmojis = (): string[] => {
  return EMOJI_CATEGORIES.flatMap(cat => cat.emojis);
};

/** Busca emojis por texto (útil para pesquisa) */
export const searchEmojis = (query: string): string[] => {
  if (!query.trim()) return [];
  
  // Busca simples por categoria label
  const lowerQuery = query.toLowerCase();
  const matchingCategories = EMOJI_CATEGORIES.filter(cat => 
    cat.label.toLowerCase().includes(lowerQuery)
  );
  
  if (matchingCategories.length > 0) {
    return matchingCategories.flatMap(cat => cat.emojis);
  }
  
  // Se não encontrou categoria, retorna primeiros emojis como sugestão
  return EMOJI_CATEGORIES[0].emojis.slice(0, 16);
};
